"use client";
import React, { FC, useMemo, useState } from "react";
import { useFormik } from "formik";
import classNames from "classnames";
import { useTranslations } from "next-intl";

import { useTheme } from "@/context/Theme.context";
import { ShowWhen, WarningBox } from "@/components/common";
import {
  SendParcelCountries,
  DeliveryTypeItem,
  DetailInputs,
  AdditionalServices,
  BuyForYou,
  CalculationResult,
} from "./";

import {
  deliveryTypesList,
  deliveryTypesListUKR,
  deliveryTypesListUZ,
} from "../utils";

import {
  Country,
  Theme,
  TransportationMode,
  TransportationType,
} from "@/enums";
import { calculateShipmentPriceUZ } from "./utils/uzbekistanCalculator";
import { calculateShipmentPriceGE } from "./utils/georgiaCalculator";
import { useGetCalculateShipmentRequest } from "@/api/useGetCalculateShipomentRequest";
import { ICalculationResult, ICalculatorDetails } from "./types";
import { GroundIcon } from "@/components/assets/icons";
import { calculateShipmentPriceURK } from "./utils/ukrainianCalculator";

const sanitizeNumberInput = (value: string): number => {
  if (!value) return NaN;
  const normalized = value.replace(",", ".");
  return Number(normalized);
};

type TProps = {
  isModal?: boolean;
  isSendParcel?: boolean;
  selectedCountryId: number;
};

const ShippingCalculator: FC<TProps> = ({
  isModal,
  isSendParcel,
  selectedCountryId,
}) => {
  const t = useTranslations();
  const theme = useTheme();

  const [isBuyForYou, setIsBuyForYou] = useState(false);

  const buyForYouHandler = () => {
    setIsBuyForYou(!isBuyForYou);
  };

  const filteredDeliveryTypes = useMemo(() => {
    if (theme === Theme.UZ) {
      // show truck and if is china
      if (selectedCountryId === Country.CHINA_LAND) {
        return deliveryTypesList.filter(
          (item) => item.type === TransportationType.GROUND,
        );
      }
      if (selectedCountryId === Country.USA) {
        return deliveryTypesListUZ;
      }
      return deliveryTypesList.filter(
        (item) => item.type === TransportationType.AIR,
      );
    }
    if (theme === Theme.GEORGIA) {
      if (selectedCountryId === Country.CHINA_AIR) {
        return deliveryTypesList
          .filter((item) => item.type === TransportationType.AIR)
          .map((item) => ({
            ...item,
            deliveryTime: "delivery_time_china_express",
          }));
      }
      if (selectedCountryId === Country.CHINA_LAND) {
        return deliveryTypesList.filter(
          (item) => item.type === TransportationType.GROUND,
        );
      }
      if (selectedCountryId === Country.GREECE) {
        return [
          {
            id: TransportationMode.AIR,
            name: TransportationType.GROUND,
            deliveryTime: "delivery_time_greece",
            icon: GroundIcon,
            type: TransportationType.AIR,
          },
        ];
      }
      if (selectedCountryId === Country.TURKEY) {
        return deliveryTypesList
          .filter((item) => item.type === TransportationType.GROUND)
          .map((item) => ({
            ...item,
            deliveryTime: "delivery_time_uz_air",
          }));
      }
      return deliveryTypesList.filter(
        (item) => item.type !== TransportationType.GROUND,
      );
    }
    if (theme === Theme.UK) {
      if (selectedCountryId === Country.CHINA_AIR) {
        return deliveryTypesListUKR.filter(
          (item) => item.type === TransportationType.AIR,
        );
      }
      return deliveryTypesListUKR;
    }
  }, [theme, selectedCountryId]);

  const formik = useFormik<ICalculatorDetails>({
    initialValues: {
      selectedDeliveryTypeId: filteredDeliveryTypes[0]?.id,
      details: {
        weight: null,
        cost: null,
        length: null,
        width: null,
        height: null,
      },
      fromCountry: selectedCountryId,
      additionalServices: [],
      toCountry: null,
      toState: null,
    },
    enableReinitialize: true,
    onSubmit: () => {},
  });

  const { data } = useGetCalculateShipmentRequest();

  const shipmentPricesData = data?.data?.data;

  const handleSelectDeliveryType = (id: number) => {
    formik.setFieldValue("selectedDeliveryTypeId", id);
  };

  const handleChangeDetails = (key: string, value: string) => {
    if (/^[\d.]*$/.test(value)) {
      formik.setFieldValue("details", {
        ...formik.values.details,
        [key]: value,
      });
    }
  };

  const handleSelectAdditionalService = (id: number) => {
    const updatedValue = formik.values.additionalServices.includes(id)
      ? formik.values.additionalServices.filter((serviceId) => serviceId !== id)
      : [...formik.values.additionalServices, id];

    formik.setFieldValue("additionalServices", updatedValue);
  };

  const calculationResult = useMemo<ICalculationResult>(() => {
    if (theme === Theme.UZ) {
      return calculateShipmentPriceUZ({
        values: {
          ...formik.values,
          details: {
            weight: Number(formik.values.details.weight),
            cost: Number(formik.values.details.cost),
            length: Number(formik.values.details.length),
            width: Number(formik.values.details.width),
            height: Number(formik.values.details.height),
          },
        },
        shipmentPricesData,
        isBuyForYou,
      });
    }
    if (theme === Theme.GEORGIA) {
      return calculateShipmentPriceGE({
        values: {
          ...formik.values,
          details: {
            weight: Number(formik.values.details.weight),
            cost: Number(formik.values.details.cost),
            length: Number(formik.values.details.length),
            width: Number(formik.values.details.width),
            height: Number(formik.values.details.height),
          },
        },
        shipmentPricesData,
      });
    }
    if (theme === Theme.UK) {
      return calculateShipmentPriceURK({
        values: {
          ...formik.values,
          details: {
            weight: Number(formik.values.details.weight),
            cost: Number(formik.values.details.cost),
            length: Number(formik.values.details.length),
            width: Number(formik.values.details.width),
            height: Number(formik.values.details.height),
          },
        },
        shipmentPricesData,
        isBuyForYou,
      });
    }
  }, [formik.values, shipmentPricesData, isBuyForYou]);

  return (
    <div
      className={classNames("h-full w-full rounded-3xl bg-white p-8", {
        "lg:max-w-[59%]": !isModal,
        "max-w-[50rem] pt-0": isModal,
      })}
    >
      <ShowWhen condition={!isModal}>
        <h1 className="text-base font-bold uppercase text-black opacity-60">
          {t("shipping_calculator")}
        </h1>
      </ShowWhen>
      <div
        className={classNames({
          "mt-6": !isSendParcel,
        })}
      >
        <h2 className="text-sm font-medium uppercase text-[#ADADAD]">
          {t("delivery_type")}
        </h2>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {filteredDeliveryTypes.map((item) => (
            <DeliveryTypeItem
              key={item.name}
              item={item}
              isSendParcel={isSendParcel}
              isActive={formik.values.selectedDeliveryTypeId === item.id}
              handleClick={handleSelectDeliveryType}
              deliveryPrice={shipmentPricesData?.deliveryPrice}
              fromCountry={formik.values.fromCountry}
              type={item.type}
              selectedCountryId={selectedCountryId}
            />
          ))}
        </div>
      </div>
      <ShowWhen condition={isSendParcel}>
        <SendParcelCountries formik={formik} />
      </ShowWhen>
      <div className="mt-6">
        <h2 className="text-sm font-medium uppercase text-[#ADADAD]">
          {t("details")}
        </h2>
        <div className="mt-3 flex gap-2">
          <DetailInputs
            isModal={isModal}
            handleChange={handleChangeDetails}
            inputValues={formik.values.details}
          />
        </div>
      </div>
      <AdditionalServices
        isModal={isModal}
        isSendParcel={isSendParcel}
        additionalServices={formik.values.additionalServices}
        handleSelectService={handleSelectAdditionalService}
      />
      <ShowWhen condition={theme === Theme.UZ}>
        <BuyForYou
          isSelected={isBuyForYou}
          onClick={buyForYouHandler}
          totalCost={calculationResult?.isBuyForYouCost}
        />
      </ShowWhen>
      <CalculationResult
        calculationResult={calculationResult}
        isSendParcel={isSendParcel}
      />
      <WarningBox text="approximate_estimate_warning" />
    </div>
  );
};

export { ShippingCalculator };
